/**
 * The outbox (`112-offline-drafts.md` §5).
 *
 * **Every mutation in the app goes through `enqueue()`. No feature calls `fetch` for a
 * write. Not one.** That is the invariant that makes "nothing the user types is ever
 * lost" a property of the ARCHITECTURE rather than a promise each feature has to
 * remember to keep.
 *
 * (Two sanctioned exceptions, both meaningless to replay: `POST /api/auth/login`, and
 * the AI SSE stream. Stated here so nobody "fixes" them.)
 *
 * The rules that are easy to break silently, all of them load-bearing:
 *
 *  - **FIFO, serial.** `page.create(X)` must land before `page.update(X)`. The
 *    auto-increment key IS the order (§5.1); a parallel drain would reorder them into
 *    404s.
 *  - **Coalescing merges the payload, never the precondition** (§5.2). Advancing
 *    `ifUnmodifiedSince` on a merge would make a stale write look fresh and defeat the
 *    entire 412 mechanism.
 *  - **A 401 PAUSES.** It does not drain into the void, does not count an attempt, does
 *    not fail an entry, and above all does not clear anything (§9).
 *  - **Nothing is ever silently dropped** (§5.4). There is no TTL, no size cap, no
 *    "clear on logout". The only way content leaves this queue unsent is a human
 *    clicking Discard with full knowledge of what is being thrown away.
 */
import { ApiError, apiFetch } from '@/api/client';
import type { PageDto } from '@/api/endpoints/pages';
import { assertNoBlobSrc } from '@/lib/content';
import {
  db,
  type EnqueueInput,
  type OutboxEntry,
  type OutboxKind,
  type OutboxRequest,
} from '@/offline/db';

/* ── Sync state (§10) ───────────────────────────────────────────────────────── */

/** Why the queue is paused. Not the same vocabulary as what the INDICATOR shows. */
export type PauseReason = 'unauthenticated' | 'offline';

/** Why the indicator says "Offline" (§10). `offline` (the pause) reads as `network` here. */
export type OfflineReason = 'network' | 'unauthenticated';

export type SyncState =
  | { kind: 'saved'; lastSavedAt: number | null }
  | { kind: 'saving'; inflight: number }
  | { kind: 'offline'; pending: number; reason: OfflineReason }
  | { kind: 'conflict'; failed: number };

/**
 * What the app does with a delivered (or permanently undeliverable) entry. Registered by
 * `OutboxBridge` at boot — the outbox may not import the QueryClient or the router
 * without a cycle, and keeping it ignorant of both is what makes it testable in Jest
 * with nothing but `fake-indexeddb`.
 */
export interface OutboxHandlers {
  onSuccess?: (entry: OutboxEntry, response: unknown) => void;
  /** A `412 stale_write` — the recovery banner's trigger (§7.4). */
  onConflict?: (entry: OutboxEntry, current: PageDto) => void;
  /** 422, a move cycle (409), or MAX_TRIES exhausted. The entry STAYS in the store. */
  onPermanentFailure?: (entry: OutboxEntry, error: ApiError) => void;
}

let handlers: OutboxHandlers = {};

export function setOutboxHandlers(next: OutboxHandlers): void {
  handlers = next;
}

/* ── Backoff (§5.3) ─────────────────────────────────────────────────────────── */

const BASE_MS = 1_000;
const MAX_MS = 60_000;
export const MAX_TRIES = 8;

/**
 * 1s 2s 4s 8s 16s 32s 60s 60s, ±25% jitter.
 *
 * Jitter matters even for ONE user: without it, ten entries that failed against a
 * restarting backend all retry at the same millisecond, hammer the container as it
 * boots, all fail again, and synchronize harder. Jitter spreads them.
 */
export function nextDelay(attempts: number, random: () => number = Math.random): number {
  const exp = Math.min(BASE_MS * 2 ** attempts, MAX_MS);
  const jitter = exp * (random() * 0.5 - 0.25);
  return Math.round(exp + jitter);
}

/* ── Coalescing (§5.2) ──────────────────────────────────────────────────────── */

/**
 * NOT coalescable: every `*.create` and `*.delete`, and `event.toggle` — an odd number
 * of toggles is not an even number, and each is a distinct intent.
 */
const COALESCABLE: ReadonlySet<OutboxKind> = new Set<OutboxKind>([
  'page.update',
  'page.move',
  'event.update',
  'settings.patch',
  'conversation.update',
  'systemPrompt.update',
]);

function union(a: string[] | undefined, b: string[] | undefined): string[] | undefined {
  if (a === undefined && b === undefined) return undefined;
  return [...new Set([...(a ?? []), ...(b ?? [])])];
}

/* ── The store ──────────────────────────────────────────────────────────────── */

const listeners = new Set<(state: SyncState) => void>();

let paused: PauseReason | null = null;
let lastSuccessAt: number | null = null;
let snapshot: SyncState = { kind: 'saved', lastSavedAt: null };
/** The in-flight drain pass, and at most one pass queued behind it. See `drain()`. */
let draining: Promise<void> | null = null;
let queuedPass: Promise<void> | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;

/** Cross-tab: every tab's indicator must agree. IndexedDB is shared; the UI is not. */
const channel: BroadcastChannel | null =
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('rn-sync');

if (channel !== null) {
  channel.onmessage = () => {
    void refresh();
  };
}

async function allEntries(): Promise<OutboxEntry[]> {
  return (await db()).getAll('outbox');
}

/**
 * §10, in priority order. `conflict` always wins, because it is the only state that
 * requires a human.
 */
function deriveState(entries: OutboxEntry[]): SyncState {
  const failed = entries.filter((e) => e.status === 'failed');
  if (failed.length > 0) return { kind: 'conflict', failed: failed.length };

  if (paused !== null) {
    return {
      kind: 'offline',
      pending: entries.length,
      reason: paused === 'unauthenticated' ? 'unauthenticated' : 'network',
    };
  }

  const inflight = entries.filter((e) => e.status === 'inflight');
  if (inflight.length > 0) return { kind: 'saving', inflight: inflight.length };

  // Offline by EVIDENCE, not by `navigator.onLine`.
  //
  // §6: "`navigator.onLine` is used as a HINT, never as truth… The only real test of
  // connectivity is a request that succeeds." The converse is what matters here: a request
  // that has actually FAILED is real evidence, and `onLine` stays `true` for the commonest
  // outage of all — the backend being down or mid-deploy while the laptop's wifi is fine.
  //
  // A literal reading of §10 would report "Saving…" throughout that outage. It is a lie, it
  // never resolves, and it is the exact opposite of the reassurance §9 asks the indicator to
  // give ("Your changes are saved on this device and will sync when you're back online").
  // §11's acceptance test agrees: with the backend stopped, the indicator must read
  // "Offline — N pending". So: any entry that has survived a failed delivery attempt means
  // we are, for practical purposes, offline.
  const attempted = entries.some((e) => e.attempts > 0);
  if (entries.length > 0 && (!navigator.onLine || attempted)) {
    return { kind: 'offline', pending: entries.length, reason: 'network' };
  }

  // Queued, and about to go. This is the honest state for the first ~second.
  if (entries.length > 0) return { kind: 'saving', inflight: 0 };

  return { kind: 'saved', lastSavedAt: lastSuccessAt };
}

function emit(): void {
  for (const listener of listeners) listener(snapshot);
}

/** Recompute the snapshot from IndexedDB and tell everyone — this tab and the others. */
async function refresh(broadcast = false): Promise<void> {
  snapshot = deriveState(await allEntries());
  emit();
  if (broadcast && channel !== null) channel.postMessage('changed');
}

export function getSyncState(): SyncState {
  return snapshot;
}

export function subscribe(listener: (state: SyncState) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The sync popover lists these (§5.4, §10). */
export async function listEntries(): Promise<OutboxEntry[]> {
  return allEntries();
}

/* ── enqueue (§5.2) ─────────────────────────────────────────────────────────── */

export async function enqueue(input: EnqueueInput): Promise<void> {
  // §8 layer 2 — the guard at the enqueue boundary. A `blob:` src must never reach the
  // server; if this throws, layer 1 (defer the save while uploads are in flight) was
  // bypassed by a code path nobody has written yet, and a loud failure now is worth a
  // permanently corrupted document later.
  const body = input.request.body;
  if (body !== null && typeof body === 'object' && 'content' in body) {
    assertNoBlobSrc((body as { content?: unknown }).content);
  }

  const database = await db();
  const tx = database.transaction('outbox', 'readwrite');
  const store = tx.objectStore('outbox');

  if (COALESCABLE.has(input.kind)) {
    const existing = await store.index('by-entity').getAll(input.entityId);
    const target = existing
      // NEVER coalesce into an `inflight` entry (§5.2): its request has already left the
      // browser, so mutating the record is either a no-op or corrupts the row we are
      // about to mark done. A `failed` one is excluded too — it is the user's to resolve.
      .filter((e) => e.kind === input.kind && e.status === 'pending')
      .at(-1);

    if (target !== undefined) {
      const mergedBody =
        typeof target.request.body === 'object' &&
        target.request.body !== null &&
        typeof input.request.body === 'object' &&
        input.request.body !== null
          ? { ...target.request.body, ...input.request.body }
          : input.request.body;

      // The merged request is built EXPLICITLY rather than by spreading `input.request` over
      // `target.request`, because the precondition must come from the target and ONLY the
      // target — including when the target has none.
      //
      // KEEP THE ORIGINAL PRECONDITION (§5.2). It records the server version the user's edit
      // was BASED on. Bumping it to "now" would claim we had seen a server version we never
      // saw, and would defeat the 412 check entirely. Coalescing merges the PAYLOAD; it never
      // touches the PRECONDITION.
      //
      // The `undefined` case is not an edge case, it is the COMMON one: a title save
      // (queued by the tree, with no precondition — a rename cannot be stale) is routinely
      // followed by a content save (queued by the editor, WITH one). A spread would have let
      // the content save's precondition win, and that precondition is stale the moment the
      // title save ahead of it lands and bumps `updated_at` — producing a 412, and a
      // recovery banner, on a page nobody but this user has ever touched.
      const request: OutboxRequest = {
        method: input.request.method,
        path: input.request.path,
        body: mergedBody,
      };
      if (target.request.ifUnmodifiedSince !== undefined) {
        request.ifUnmodifiedSince = target.request.ifUnmodifiedSince;
      }

      const merged: OutboxEntry = {
        ...target,
        request,
        ...(input.label === undefined ? {} : { label: input.label }),
      };

      const waits = union(target.waitsOnUploads, input.waitsOnUploads);
      if (waits !== undefined) merged.waitsOnUploads = waits;

      await store.put(merged);
      await tx.done;
      await refresh(true);
      void drain();
      return;
    }
  }

  await store.add({
    ...input,
    status: 'pending',
    attempts: 0,
    createdAt: Date.now(),
    nextAttemptAt: Date.now(),
  } as OutboxEntry);

  await tx.done;
  await refresh(true);
  void drain();
}

/* ── Pause / resume (§9) ────────────────────────────────────────────────────── */

export function pause(reason: PauseReason): void {
  paused = reason;
  // Entries stay in IndexedDB, in order, with their attempt counts untouched. A 401 is a
  // failure of the SESSION, not of the mutation.
  void refresh();
}

export function resume(): void {
  paused = null;
  void refresh().then(() => drain());
}

export function isPaused(): PauseReason | null {
  return paused;
}

/* ── The drain (§5.1, §5.5, §6) ─────────────────────────────────────────────── */

async function put(entry: OutboxEntry): Promise<void> {
  await (await db()).put('outbox', entry);
}

async function remove(id: number): Promise<void> {
  await (await db()).delete('outbox', id);
}

function isCreate(kind: OutboxKind): boolean {
  return kind.endsWith('.create');
}

/** Schedule the next drain at the earliest `nextAttemptAt`. Not a poll: an idle app with
 *  an empty queue schedules nothing and burns no CPU (§6). */
async function scheduleNext(): Promise<void> {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  if (paused !== null) return;

  const pending = (await allEntries()).filter((e) => e.status === 'pending');
  if (pending.length === 0) return;

  const soonest = Math.min(...pending.map((e) => e.nextAttemptAt));
  const delay = Math.max(0, soonest - Date.now());

  timer = setTimeout(() => {
    timer = null;
    void drain();
  }, delay);
}

/** A successful request after a failure means the backend is evidently back. Do not make
 *  the user wait out a 60-second backoff for a server that is already answering (§6). */
async function unblockAfterSuccess(): Promise<void> {
  const database = await db();
  const tx = database.transaction('outbox', 'readwrite');
  const store = tx.objectStore('outbox');
  const now = Date.now();

  for (const entry of await store.getAll()) {
    if (entry.status === 'pending' && entry.nextAttemptAt > now) {
      await store.put({ ...entry, nextAttemptAt: now });
    }
  }
  await tx.done;
}

async function deliver(entry: OutboxEntry): Promise<unknown> {
  const headers: Record<string, string> = {};
  if (entry.request.ifUnmodifiedSince !== undefined) {
    headers['If-Unmodified-Since'] = entry.request.ifUnmodifiedSince;
  }

  return apiFetch<unknown>(entry.request.path, {
    method: entry.request.method,
    headers,
    ...(entry.request.body === undefined ? {} : { body: JSON.stringify(entry.request.body) }),
  });
}

/** Mark an entry permanently failed. It STAYS in the store, body intact (§5.4). */
async function markFailed(entry: OutboxEntry, error: ApiError, current?: PageDto): Promise<void> {
  const failed: OutboxEntry = {
    ...entry,
    status: 'failed',
    lastError: { status: error.status, code: error.code, message: error.message },
  };
  if (current !== undefined) failed.stashedCurrent = current;

  await put(failed);
  handlers.onPermanentFailure?.(failed, error);
}

async function retryLater(entry: OutboxEntry): Promise<void> {
  const attempts = entry.attempts + 1;

  if (attempts >= MAX_TRIES) {
    await put({
      ...entry,
      status: 'failed',
      attempts,
      lastError: entry.lastError ?? {
        status: 0,
        code: 'server_error',
        message: 'Gave up after 8 attempts.',
      },
    });
    return;
  }

  await put({
    ...entry,
    status: 'pending',
    attempts,
    nextAttemptAt: Date.now() + nextDelay(attempts),
  });
}

/**
 * One entry. Returns `false` to stop the drain entirely (a pause).
 *
 * The response table is `112-offline-drafts.md` §5.5, implemented line for line.
 */
async function process(entry: OutboxEntry): Promise<boolean> {
  await put({ ...entry, status: 'inflight' });
  await refresh(true);

  try {
    const response = await deliver(entry);
    await remove(entry.id);
    lastSuccessAt = Date.now();
    handlers.onSuccess?.(entry, response);
    await unblockAfterSuccess();
    return true;
  } catch (error) {
    if (!(error instanceof ApiError)) {
      // A network error, or `fetch` rejecting. If the OS says we are offline, pause
      // rather than burning attempts on a queue that cannot possibly drain (§5.5).
      if (!navigator.onLine) {
        await put({ ...entry, status: 'pending' });
        pause('offline');
        return false;
      }
      await retryLater(entry);
      return true;
    }

    switch (error.status) {
      case 401:
        // NOT the entry's fault. No attempt counted, nothing failed, nothing dropped.
        // `apiFetch` has already run the app's 401 path; we simply stop.
        await put({ ...entry, status: 'pending' });
        pause('unauthenticated');
        return false;

      case 403:
        // `apiFetch` already refreshed the CSRF token from /api/auth/me and retried
        // exactly once (§5.5). Reaching here means that retry also failed — the session
        // is gone, not merely stale. Treat as a 401.
        await put({ ...entry, status: 'pending' });
        pause('unauthenticated');
        return false;

      case 409:
        if (isCreate(entry.kind)) {
          // THE REPLAY RULE. The row already exists: a previous attempt landed and we
          // lost the response. `040-api-contract.md` mandates treating this as success.
          await remove(entry.id);
          lastSuccessAt = Date.now();
          handlers.onSuccess?.(entry, undefined);
          return true;
        }
        // A move that would have created a cycle. Permanent; roll the tree back.
        await markFailed(entry, error);
        return true;

      case 412: {
        // The local edit was based on a version the server has since moved past. Do NOT
        // retry — a retry would clobber. Same path as a reload-time divergence (§7.4).
        const current = (error.body as { current?: PageDto } | null)?.current;
        await markFailed(entry, error, current);
        if (current !== undefined) handlers.onConflict?.({ ...entry, status: 'failed' }, current);
        return true;
      }

      case 422:
        // The body is invalid and will not become valid on a retry.
        await markFailed(entry, error);
        return true;

      case 429: {
        const delay =
          error.retryAfter !== undefined ? error.retryAfter * 1_000 : nextDelay(entry.attempts);
        await put({
          ...entry,
          status: 'pending',
          attempts: entry.attempts + 1,
          nextAttemptAt: Date.now() + delay,
        });
        return true;
      }

      default:
        if (error.status >= 500) {
          await retryLater({
            ...entry,
            lastError: { status: error.status, code: error.code, message: error.message },
          });
          return true;
        }
        // Any other 4xx is permanent — it does not get better by asking again.
        await markFailed(entry, error);
        return true;
    }
  }
}

async function drainLocked(): Promise<void> {
  for (;;) {
    if (paused !== null) return;

    const entries = (await allEntries()).sort((a, b) => a.id - b.id);

    // A permanently failed entry does not block the ones behind it — but entries for the
    // SAME entity do queue behind it. Replaying `page.update(X)` when `page.create(X)`
    // has permanently failed would just produce a 404 for every subsequent edit (§5.1).
    const blocked = new Set(entries.filter((e) => e.status === 'failed').map((e) => e.entityId));

    const now = Date.now();
    const next = entries.find(
      (e) => e.status === 'pending' && e.nextAttemptAt <= now && !blocked.has(e.entityId),
    );

    if (next === undefined) {
      await refresh(true);
      await scheduleNext();
      return;
    }

    const proceed = await process(next);
    await refresh(true);

    if (!proceed) return;
  }
}

async function runPass(): Promise<void> {
  // Multi-tab: two open tabs must not process the same entry twice (§6).
  if (typeof navigator !== 'undefined' && navigator.locks !== undefined) {
    await navigator.locks.request('rn-outbox', async () => {
      await drainLocked();
    });
  } else {
    await drainLocked();
  }
}

/**
 * Drain the queue, and RESOLVE ONLY WHEN IT HAS ACTUALLY BEEN DRAINED.
 *
 * Two things this must get right, both of which a naive `if (draining) return;` gets
 * wrong:
 *
 *  1. **Awaiting it must mean something.** `drainToCompletion()` (before an explicit
 *     logout) checks the queue after awaiting this. Returning early while a drain is still
 *     running would have it inspect a queue mid-flight and report success too soon.
 *  2. **An entry enqueued while a pass is finishing must not be stranded.** Joining the
 *     in-flight pass is not enough: it may already be past the point where it would have
 *     seen the new row, and it would then resolve having never sent it. So a caller that
 *     arrives during a pass gets a FRESH pass chained behind it.
 *
 * At most one pass runs and one waits, so a burst of keystrokes cannot chain a hundred of
 * them.
 */
export function drain(): Promise<void> {
  if (paused !== null) return Promise.resolve();

  if (draining === null) {
    const pass = runPass().finally(() => {
      if (draining === pass) draining = null;
    });
    draining = pass;
    return pass;
  }

  // A pass is running. If one is already queued behind it, that one will see our entry.
  if (queuedPass !== null) return queuedPass;

  const next = draining.then(async () => {
    queuedPass = null;
    await drain();
  });
  queuedPass = next;

  return next;
}

/** Used before an explicit logout (`111` §8.2) — the ONE case where we drain first. */
export async function drainToCompletion(): Promise<void> {
  await drain();
  const left = (await allEntries()).filter((e) => e.status === 'pending');
  if (left.length > 0) throw new Error(`${left.length} change(s) could not be synced.`);
}

/* ── The two human decisions (§5.4) ─────────────────────────────────────────── */

/** Reset a failed entry and re-queue it. Its body was never touched. */
export async function retry(id: number): Promise<void> {
  const entry = await (await db()).get('outbox', id);
  if (entry === undefined) return;

  const reset: OutboxEntry = {
    ...entry,
    status: 'pending',
    attempts: 0,
    nextAttemptAt: Date.now(),
  };
  delete reset.lastError;
  delete reset.stashedCurrent;

  await put(reset);
  await refresh(true);
  void drain();
}

export async function retryAll(): Promise<void> {
  const failed = (await allEntries()).filter((e) => e.status === 'failed');
  for (const entry of failed) await retry(entry.id);
}

/**
 * The ONLY way an entry ever leaves while unsent — an explicit human decision, taken with
 * full knowledge of what is being thrown away (§5.4). Any OTHER code path that deletes an
 * unsent entry is a bug, and it is *the* bug the whole spec exists to prevent.
 */
export async function discard(id: number): Promise<void> {
  await remove(id);
  await refresh(true);
}

/** Cancel a queued `page.update` — the "Use server version" branch of the banner (§7.3). */
export async function discardPageUpdates(pageId: string): Promise<void> {
  const database = await db();
  const tx = database.transaction('outbox', 'readwrite');
  const store = tx.objectStore('outbox');

  for (const entry of await store.index('by-entity').getAll(pageId)) {
    if (entry.kind === 'page.update' && entry.status !== 'inflight') {
      await store.delete(entry.id);
    }
  }
  await tx.done;
  await refresh(true);
}

/* ── Drain triggers (§6) ────────────────────────────────────────────────────── */

let started = false;

/**
 * Un-strand entries left `inflight` by a tab that died mid-request.
 *
 * `inflight` means "this request has left the browser and we are waiting". If the tab is
 * closed, reloaded, or crashes at that exact moment, nothing ever writes the outcome back —
 * and the drain only ever picks up `pending` rows, so the entry would sit there **forever**,
 * with the sync indicator honestly but uselessly reporting "Saving…" for the rest of time.
 * That is a silent loss of the user's words, which is the one thing this file exists to
 * prevent.
 *
 * So on every app start, any `inflight` row is put back to `pending` and retried. It may
 * have actually landed the first time — and that is exactly why the replay rules in §5.5 are
 * built the way they are: a replayed `*.create` gets a `409`, which is treated as SUCCESS,
 * and every other verb we queue (`PATCH`, `move`, `archive`, `DELETE`) is idempotent. A
 * duplicate delivery is harmless; a stranded entry is not.
 */
export async function recoverInflight(): Promise<void> {
  const database = await db();
  const tx = database.transaction('outbox', 'readwrite');
  const store = tx.objectStore('outbox');

  for (const entry of await store.getAll()) {
    if (entry.status === 'inflight') {
      // The attempt count is NOT incremented: we do not know that the request failed, only
      // that we stopped listening. It is not the entry's fault the tab was closed.
      await store.put({ ...entry, status: 'pending', nextAttemptAt: Date.now() });
    }
  }
  await tx.done;
}

/**
 * Called once, after the session query resolves. Anything left over from the last session
 * goes out immediately.
 *
 * `navigator.onLine` is a HINT, never truth: it reports "connected to *a* network", which
 * is `true` on captive-portal hotel wifi that drops every request. `false` → certainly
 * offline. `true` → *maybe*; try anyway. The only real test of connectivity is a request
 * that succeeds.
 */
export function startOutbox(): void {
  if (started) return;
  started = true;

  window.addEventListener('online', () => {
    if (paused === 'offline') resume();
    else void drain();
  });

  window.addEventListener('offline', () => {
    void refresh();
  });

  document.addEventListener('visibilitychange', () => {
    // The tab was backgrounded; timers may have been throttled.
    if (document.visibilityState === 'visible') void drain();
  });

  // Recover first, THEN drain — otherwise the first pass would walk straight past the very
  // entries the last session stranded.
  void recoverInflight()
    .then(() => refresh())
    .then(() => drain());
}

export const outbox = {
  enqueue,
  drain,
  drainToCompletion,
  pause,
  resume,
  isPaused,
  retry,
  retryAll,
  discard,
  discardPageUpdates,
  subscribe,
  getSyncState,
  listEntries,
  start: startOutbox,
};
